import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PdfUpload } from '../../components/common/PdfUpload';
import { updatePlayerProfile, uploadRecommendation, uploadVideo, deleteVideo } from '../../features/playerProfile/playerProfileSlice';
import { VideoPlayerModal } from '../../components/VideoPlayer/VideoPlayerModal';
import { VideoThumbnail } from '../../components/VideoPlayer/VideoThumbnail';

export const 
LevelUpProfileModal = ({ isOpen, onClose, playerName = 'Player' }) => {
  const dispatch = useDispatch();
  const { profile, updateLoading, videoLoading, recommendationLoading } = useSelector((state) => state.playerProfile);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    title: '',
    description: '',
    position: 'Pitcher',
    tags: [],
    strengthInput: '',
    coachRecommendation: null,
    awards: []
  });

  const [strengthInput, setStrengthInput] = useState('');
  const [awardInput, setAwardInput] = useState('');
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [deletingVideoId, setDeletingVideoId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Trigger animation after modal is mounted
      setTimeout(() => setShowModal(true), 10);

      // Load profile data when modal opens
      if (profile) {
        setFormData({
          email: profile.email || '',
          title: profile.title || '',
          description: profile.description || '',
          position: profile.primaryPosition || 'Pitcher',
          tags: profile.strengths || [],
          strengthInput: '',
          coachRecommendation: profile.coachRecommendation || null,
          awards: profile.awardsAchievements || []
        });
      }
    } else {
      setShowModal(false);
    }
  }, [isOpen, profile]);

  // Sync uploaded videos with profile
  useEffect(() => {
    if (profile?.videos) {
      setUploadedVideos(profile.videos);
    }
  }, [profile?.videos]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddStrength = () => {
    if (strengthInput.trim()) {
      setFormData({ ...formData, tags: [...formData.tags, strengthInput.trim()] });
      setStrengthInput('');
    }
  };

  const handleStrengthKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddStrength();
    }
  };

  const handleRemoveStrength = (index) => {
    setFormData({ ...formData, tags: formData.tags.filter((_, i) => i !== index) });
  };

  const handleAddAward = () => {
    if (awardInput.trim()) {
      setFormData({ ...formData, awards: [...formData.awards, awardInput.trim()] });
      setAwardInput('');
    }
  };

  const handleAwardKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAward();
    }
  };

  const handleRemoveAward = (index) => {
    setFormData({ ...formData, awards: formData.awards.filter((_, i) => i !== index) });
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await dispatch(uploadVideo(file));
    }
  };

  const handleVideoDelete = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      setDeletingVideoId(videoId);
      dispatch(deleteVideo(videoId));
      setUploadedVideos((prev) => prev.filter((v) => v._id !== videoId));
      setDeletingVideoId(null);
    }
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setIsPlayerOpen(true);
  };

  const handleFileUpload = (file) => {
    setFormData({ ...formData, coachRecommendation: file });
  };

  const handleClose = () => {
    setShowModal(false);
    setTimeout(onClose, 300); // Wait for animation to complete
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Update basic profile info with strengths and awards
    const profileData = {
      email: formData.email,
      title: formData.title,
      description: formData.description,
      primaryPosition: formData.position,
      strengths: formData.tags,
      awardsAchievements: formData.awards
    };

    await dispatch(updatePlayerProfile(profileData));

    // Upload coach recommendation if new file selected
    if (formData.coachRecommendation && formData.coachRecommendation instanceof File) {
      await dispatch(uploadRecommendation(formData.coachRecommendation));
    }

    handleClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          showModal ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="level-up-title"
        className={`relative bg-jp-card rounded-2xl p-6 sm:p-10 max-w-2xl w-full shadow-2xl border border-jp-outline/70 my-8 max-h-[90vh] overflow-y-auto text-left transition-all duration-300 ${
        showModal ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      }`}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-1 rounded-lg text-jp-muted hover:text-jp-ink hover:bg-jp-section transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-jp-accent dark:text-jp-gold text-sm font-bold uppercase tracking-widest mb-3">
            {playerName}
          </p>
          <h2 id="level-up-title" className="text-4xl sm:text-5xl font-anton leading-[1.1] tracking-tight text-jp-ink">
            LEVEL UP YOUR PROFILE
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="rounded-xl p-5 sm:p-6 bg-jp-section border border-jp-outline/50 space-y-5">

          {/* Email Input */}
          <div>
            <label className="block text-sm font-semibold text-jp-ink mb-1.5">Email</label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                className="w-full px-4 py-3 rounded-xl bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent pr-10 transition-all"
                required
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-jp-ink mb-1.5">Title</label>
            <div className="relative">
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter a title"
                maxLength={100}
                className="w-full px-4 py-3 rounded-xl bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition-all"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-jp-muted text-xs">
                {formData.title.length}/100
              </span>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-jp-ink mb-1.5">Description</label>
            <div className="relative">
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter a description"
                maxLength={100}
                rows="3"
                className="w-full px-4 py-3 rounded-xl bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent resize-none transition-all"
              />
              <span className="absolute right-3 bottom-3 text-jp-muted text-xs">
                {formData.description.length}/100
              </span>
            </div>
          </div>

          {/* Add Strengths */}
          <div>
            <label className="block text-sm font-semibold text-jp-ink mb-1.5">Add strengths <span className="font-normal text-jp-muted text-xs">Press Enter to add</span></label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={strengthInput}
                onChange={(e) => setStrengthInput(e.target.value)}
                onKeyPress={handleStrengthKeyPress}
                placeholder="Enter a strength"
                className="flex-1 px-4 py-3 rounded-xl bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition-all"
              />
              <button
                type="button"
                onClick={handleAddStrength}
                className="px-6 py-3 rounded-xl border border-jp-accent/50 bg-jp-accent/10 text-jp-accent font-semibold hover:bg-jp-accent/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent"
              >
                Add
              </button>
            </div>
            {/* Display Strengths */}
            <label className="block text-sm font-semibold text-jp-ink mb-1.5">Your strengths</label>
            <div className="flex flex-wrap gap-2 p-3 rounded-xl bg-jp-field border border-jp-line min-h-[60px]">
              {formData.tags.map((tag, index) => (
                <span key={index} className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 dark:text-emerald-400 rounded-full text-sm font-medium flex items-center gap-2 whitespace-nowrap">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveStrength(index)}
                    className="hover:text-red-500 transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Video Upload Section */}
          <div>
            <label className="block text-sm font-semibold text-jp-ink mb-1.5">Videos</label>

            {/* Uploaded Videos Grid */}
            {uploadedVideos.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-3">
                {uploadedVideos.map((video) => (
                  <div key={video._id} className="relative group aspect-video bg-jp-band rounded-lg overflow-hidden">
                    <VideoThumbnail
                      videoSrc={video.videoUrl}
                      thumbnailUrl={video.thumbnailUrl}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleVideoClick(video)}
                      className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleVideoDelete(video._id)}
                      disabled={deletingVideoId === video._id}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    >
                      {deletingVideoId === video._id ? (
                        <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Button */}
            <label className={`flex items-center justify-center px-8 py-3 rounded-xl border-2 border-dashed border-jp-accent/60 bg-jp-field text-jp-accent font-bold text-sm uppercase tracking-wider transition-colors focus-within:ring-2 focus-within:ring-jp-accent ${
              videoLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-jp-accent/10 cursor-pointer'
            }`}>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="sr-only"
                disabled={videoLoading}
              />
              {videoLoading && (
                <div className="w-4 h-4 mr-2 border-2 border-jp-accent border-t-transparent rounded-full animate-spin"></div>
              )}
              {uploadedVideos.length > 0 ? 'UPLOAD ANOTHER VIDEO' : 'UPLOAD VIDEO'}
            </label>
          </div>

          {/* Video Player Modal */}
          <VideoPlayerModal
            isOpen={isPlayerOpen}
            onClose={() => { setIsPlayerOpen(false); setSelectedVideo(null); }}
            video={selectedVideo}
            profile={profile}
            allVideos={uploadedVideos}
          />

          {/* Coach Recommendation */}
          <PdfUpload
            label="Coach recommendation (PDF)"
            file={formData.coachRecommendation}
            onFileChange={handleFileUpload}
            accept="image/*,.pdf"
            placeholder="Drop your file here or click to browse"
            themed
          />

          {/* Awards */}
          <div>
            <label className="block text-sm font-semibold text-jp-ink mb-1.5">
              Awards &amp; achievements <span className="font-normal text-jp-muted text-xs">Press Enter to add</span>
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={awardInput}
                onChange={(e) => setAwardInput(e.target.value)}
                onKeyPress={handleAwardKeyPress}
                placeholder="Enter an award or achievement"
                className="flex-1 px-4 py-3 rounded-xl bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition-all"
              />
              <button
                type="button"
                onClick={handleAddAward}
                className="px-6 py-3 rounded-xl border border-jp-accent/50 bg-jp-accent/10 text-jp-accent font-semibold hover:bg-jp-accent/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent"
              >
                Add
              </button>
            </div>
            {/* Display Awards */}
            <div className="flex flex-wrap gap-2.5 p-3 rounded-xl bg-jp-field border border-jp-line min-h-[60px]">
              {formData.awards.map((award, index) => (
                <span key={index} className="px-4 py-2 bg-jp-accent/10 border border-jp-accent/40 text-jp-accent rounded-full text-sm font-semibold flex items-center gap-2.5">
                  {award}
                  <button
                    type="button"
                    onClick={() => handleRemoveAward(index)}
                    className="hover:text-red-500 transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="pt-3">
            <button
              type="submit"
              disabled={updateLoading}
              className="w-full py-4 text-base font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-slate-900 rounded-xl hover:brightness-110 transition-all shadow-lg shadow-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-section active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {updateLoading && (
                <div className="w-4 h-4 mr-2 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
              )}
              Next
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LevelUpProfileModal;
